import { useCallback, useMemo, useState } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { Cluster, ClusterInput, clusterByPixelRadius } from '../utils/cluster';

/**
 * Pixel separation below which two map objects are treated as overlapping.
 * The planner's waypoint badge is 30px across, so anything closer than this is
 * literally drawn on top of its neighbour (issue #434).
 */
export const CLUSTER_RADIUS_PX = 30;

/** A map object to cluster: an identity, a position, and the object itself. */
export interface ClusterCandidate<T> {
  id: string;
  lat: number;
  lng: number;
  item: T;
}

/**
 * Cluster map objects by their on-screen separation at the current zoom.
 *
 * Positions are projected with `map.project` at the current zoom — absolute
 * world pixels — so panning never reshuffles the clusters; only a zoom change
 * does, which is when this recomputes.
 */
export const useClusters = <T>(
  candidates: ReadonlyArray<ClusterCandidate<T>>,
  radiusPx: number = CLUSTER_RADIUS_PX
): Cluster<T>[] => {
  const map = useMap();
  // Re-cluster when the zoom changes; a pan cannot change the result.
  const [zoom, setZoom] = useState(() => map.getZoom());
  const syncZoom = useCallback(() => setZoom(map.getZoom()), [map]);
  useMapEvents({ zoomend: syncZoom });

  return useMemo(() => {
    const projected: ClusterInput<T>[] = candidates.map((c) => {
      const point = map.project([c.lat, c.lng], zoom);
      return { ...c, x: point.x, y: point.y };
    });
    return clusterByPixelRadius(projected, radiusPx);
  }, [candidates, map, radiusPx, zoom]);
};
